// 认证服务 API 客户端（与 kcc-web 同源 whoami 策略一致）
import axios, { AxiosInstance } from 'axios';

const KRATOS_PUBLIC_URL = process.env.REACT_APP_KRATOS_PUBLIC_URL || 'http://localhost:8080/api';

const AUTH_LOGIN_URL =
  process.env.REACT_APP_AUTH_LOGIN_URL ||
  process.env.REACT_APP_AUTH_SERVICE_URL ||
  'http://localhost:3001/auth';

const authApi: AxiosInstance = axios.create({
  baseURL: KRATOS_PUBLIC_URL,
  timeout: 10000,
  withCredentials: true,
});

export interface Session {
  id: string;
  active?: boolean;
  expires_at?: string;
  authenticated_at?: string;
  authenticator_assurance_level?: string;
  identity: {
    id: string;
    schema_id?: string;
    schema_url?: string;
    traits: {
      email?: string;
      username?: string;
      name?: {
        first?: string;
        last?: string;
      };
      [key: string]: any;
    };
  };
}

function normalizeWhoamiResponse(data: any): Session | null {
  if (!data || typeof data !== 'object') return null;
  const raw = (data as Record<string, unknown>).session && typeof (data as Record<string, unknown>).session === 'object'
    ? (data as Record<string, unknown>).session as Record<string, unknown>
    : (data as Record<string, unknown>);
  if (raw.identity && typeof raw.identity === 'object') {
    const identity = raw.identity as Record<string, unknown>;
    if (identity.id) {
      return {
        id: String(raw.id ?? ''),
        active: raw.active as boolean | undefined,
        expires_at: raw.expires_at as string | undefined,
        authenticated_at: raw.authenticated_at as string | undefined,
        authenticator_assurance_level: raw.authenticator_assurance_level as string | undefined,
        identity: {
          id: String(identity.id),
          schema_id: identity.schema_id as string | undefined,
          schema_url: identity.schema_url as string | undefined,
          traits: (identity.traits && typeof identity.traits === 'object' ? identity.traits : {}) as Session['identity']['traits'],
        },
      };
    }
  }
  const identityId = raw.identity_id ?? (raw as any).identityId;
  if (identityId) {
    return {
      id: String(raw.id ?? ''),
      active: raw.active as boolean | undefined,
      expires_at: raw.expires_at as string | undefined,
      authenticated_at: raw.authenticated_at as string | undefined,
      authenticator_assurance_level: raw.authenticator_assurance_level as string | undefined,
      identity: {
        id: String(identityId),
        traits: (raw.traits && typeof raw.traits === 'object' ? raw.traits : {}) as Session['identity']['traits'],
      },
    };
  }
  return null;
}

const SESSION_CACHE_MS = 30_000;
let sessionCache: { session: Session | null; expiresAt: number } | null = null;

export const getCurrentSession = async (): Promise<Session | null> => {
  const now = Date.now();
  if (sessionCache && sessionCache.expiresAt > now) {
    return sessionCache.session;
  }
  try {
    const response = await authApi.get('/sessions/whoami', {
      headers: {
        Accept: 'application/json',
      },
    });
    const session = normalizeWhoamiResponse(response.data);
    sessionCache = { session, expiresAt: now + SESSION_CACHE_MS };
    return session;
  } catch (error: any) {
    if (error?.response?.status === 401) {
      sessionCache = { session: null, expiresAt: now + SESSION_CACHE_MS };
      return null;
    }
    throw error;
  }
};

export const invalidateSessionCache = (): void => {
  sessionCache = null;
};

export const getLoginUrl = (): string => {
  let base = AUTH_LOGIN_URL.replace(/\/$/, '');
  if (/\/login$/i.test(base)) {
    return base;
  }
  if (/^https?:\/\/localhost:3001$/i.test(base) || /^https?:\/\/127\.0\.0\.1:3001$/i.test(base)) {
    base = `${base}/auth`;
  }
  return `${base}/login`;
};

export const buildLoginRedirectUrl = (currentHref?: string): string => {
  const loginUrl = getLoginUrl();
  const returnUrl = encodeURIComponent(currentHref ?? window.location.href);
  return `${loginUrl}?returnUrl=${returnUrl}`;
};

export const getUserDisplayName = (session: Session): string => {
  const traits = session.identity?.traits;
  if (!traits) return '未知用户';
  const { name, email, username } = traits;
  if (name?.last || name?.first) {
    return [name.first, name.last].filter(Boolean).join(' ').trim();
  }
  return username ?? email ?? '未知用户';
};

export const logoutSession = async (returnTo?: string): Promise<void> => {
  invalidateSessionCache();
  const returnToUrl = returnTo || window.location.origin + '/';

  try {
    const flowRes = await authApi.get('/self-service/logout/browser', {
      params: { return_to: returnToUrl },
      headers: { Accept: 'application/json' },
      withCredentials: true,
    });

    const logoutToken: string | undefined = flowRes.data?.logout_token;
    const logoutUrl: string | undefined = flowRes.data?.logout_url;

    if (logoutToken) {
      await authApi.get('/self-service/logout', {
        params: {
          token: logoutToken,
          return_to: returnToUrl,
        },
        headers: { Accept: 'application/json' },
        withCredentials: true,
        maxRedirects: 0,
        validateStatus: (status) => status === 204 || (status >= 200 && status < 400),
      });
      return;
    }

    if (logoutUrl) {
      const apiLogoutUrl = logoutUrl.includes('/api/self-service/')
        ? logoutUrl
        : logoutUrl.replace('/self-service/', '/api/self-service/');
      window.location.href = apiLogoutUrl;
      return;
    }

    throw new Error('未获取到有效的登出凭证');
  } catch (error: any) {
    if (error?.response?.status === 401) {
      return;
    }
    throw error;
  }
};
